import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, EmptyState, ErrorState, Skeleton, TextField, Pill } from "../../components/ui.tsx";
import { ConfirmDialog, Modal } from "../../components/dialog.tsx";
import { get, patch, post, put, type ApiError } from "../../lib/api.ts";
import { planKey, usePlan } from "../../lib/plan.tsx";
import { roleLabel, type Group, type Membership } from "../../lib/types.ts";
import { useCommand } from "./shared.tsx";
import { useUrlState } from "../../lib/nav.tsx";
import { ArchivedToggle } from "./shared.tsx";

export function GroupsTab() {
  const plan = usePlan();
  const [view, setView] = useUrlState({ arquivados: "" });
  const showArchived = view.arquivados === "1";
  const groups = useQuery({
    queryKey: planKey(plan.id, "groups", showArchived ? "with-archived" : "active"),
    queryFn: async () => (await get<{ data: Group[] }>(`/plans/${plan.id}/groups${showArchived ? "?include_archived=true" : ""}`)).data,
  });
  const [naming, setNaming] = useState<Group | "new" | null>(null);
  const [members, setMembers] = useState<Group | null>(null);
  const [manager, setManager] = useState<Group | null>(null);
  const [archiving, setArchiving] = useState<Group | null>(null);
  const { run, busy } = useCommand(plan.id);

  if (groups.isPending) return <Skeleton lines={4} />;
  if (groups.isError) return <ErrorState error={groups.error} onRetry={() => void groups.refetch()} />;
  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted">Todos os participantes consultam o plano inteiro; cada grupo edita os eixos e as ações da agenda atribuídos a ele.</p>
        <div className="flex flex-wrap items-center gap-3"><ArchivedToggle checked={showArchived} onChange={(v) => setView({ arquivados: v ? "1" : "" })} /><Button onClick={() => setNaming("new")}>Novo grupo</Button></div>
      </div>
      {groups.data.length === 0 ? (
        <EmptyState title="Ainda não há grupos de trabalho." action={<Button onClick={() => setNaming("new")}>Criar grupo</Button>}>
          Crie grupos, atribua eixos a eles e inclua participantes.
        </EmptyState>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {groups.data.map((g) => (
            <li key={g.id} className={`rounded-panel border border-line bg-surface p-4 shadow-e1 ${g.archived_at ? "opacity-70" : ""}`}>
              <p className="font-semibold">{g.name} {g.archived_at && <Pill tone="gray">Arquivado</Pill>}</p>
              <p className="text-xs text-muted">{g.member_count} membro(s) · {g.axis_count} eixo(s) · {g.event_count} ação(ões) da agenda</p>
              <p className="mt-2 text-sm">{g.members.length ? g.members.map((m) => m.name).join(", ") : <span className="text-muted">Sem membros.</span>}</p>
              <p className="mt-1 text-xs text-muted">Gestor: {g.members.find((m) => m.membership_id === g.manager_membership_id)?.name ?? "não definido"}</p>
              {!g.archived_at && <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => setMembers(g)}>Membros</Button>
                <Button variant="secondary" onClick={() => setManager(g)} disabled={g.members.length === 0}>Gestor</Button>
                <Button variant="secondary" onClick={() => setNaming(g)}>Renomear</Button>
                <Button variant="dangerGhost" onClick={() => setArchiving(g)}>Arquivar</Button>
              </div>}
            </li>
          ))}
        </ul>
      )}
      {naming && <GroupNameDialog group={naming === "new" ? null : naming} onClose={() => setNaming(null)} />}
      {members && <MembersDialog group={members} onClose={() => setMembers(null)} />}
      {manager && <ManagerDialog group={manager} onClose={() => setManager(null)} />}
      <ConfirmDialog open={archiving !== null} onOpenChange={(o) => !o && setArchiving(null)} title="Arquivar grupo?" confirmLabel="Arquivar" danger loading={busy}
        onConfirm={async () => {
          await run(() => post(`/plans/${plan.id}/groups/${archiving!.id}/archive`, { expected_version: archiving!.version }), "Grupo arquivado.");
          setArchiving(null);
        }}>
        <p className="text-sm">Antes de arquivar, remova os membros, transfira eixos e ações da agenda e ajuste convites pendentes deste grupo.</p>
      </ConfirmDialog>
    </>
  );
}

function GroupNameDialog({ group, onClose }: { group: Group | null; onClose: () => void }) {
  const plan = usePlan();
  const { run, busy } = useCommand(plan.id);
  const [name, setName] = useState(group?.name ?? "");
  const [error, setError] = useState<string>();
  const save = async () => {
    if (!name.trim()) return setError("Informe o nome do grupo.");
    const ok = await run(
      () => (group ? patch(`/plans/${plan.id}/groups/${group.id}`, { name, expected_version: group.version }) : post(`/plans/${plan.id}/groups`, { name })),
      group ? "Grupo atualizado." : "Grupo criado.",
      (e: ApiError) => {
        if (e.fieldErrors[0]) setError(e.fieldErrors[0].message);
        return e.fieldErrors.length > 0;
      },
    );
    if (ok) onClose();
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={group ? "Renomear grupo" : "Novo grupo"}
      footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button onClick={() => void save()} loading={busy}>Salvar</Button></>}>
      <form noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <TextField label="Nome do grupo" required autoFocus value={name} onChange={(e) => setName(e.target.value)} error={error} />
      </form>
    </Modal>
  );
}

/** BLD-28: the manager validates the diagnosis categories answered by members of this group. */
function ManagerDialog({ group, onClose }: { group: Group; onClose: () => void }) {
  const plan = usePlan();
  const { run, busy } = useCommand(plan.id);
  const [selected, setSelected] = useState(group.manager_membership_id ?? "");
  const save = async () => {
    const ok = await run(() => put(`/plans/${plan.id}/groups/${group.id}/manager`, { expected_version: group.version, membership_id: selected || null }), "Gestor atualizado.");
    if (ok) onClose();
  };
  const options = [{ id: "", name: "Sem gestor", hint: "As respostas enviadas vão direto para a aprovação do consultor." },
    ...group.members.map((m) => ({ id: m.membership_id, name: m.name, hint: roleLabel[m.role] }))];
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Gestor de ${group.name}`}
      description="O gestor valida (ou devolve) as respostas do diagnóstico institucional dadas pelos membros deste grupo, antes da aprovação do consultor."
      footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button onClick={() => void save()} loading={busy}>Salvar</Button></>}>
      <fieldset>
        <legend className="sr-only">Gestor do grupo</legend>
        <ul className="divide-y divide-line rounded-ctl border border-line">
          {options.map((o) => (
            <li key={o.id || "none"}>
              <label className="elevate flex cursor-pointer items-center gap-3 px-3 py-2.5">
                <input type="radio" name="manager" className="size-4 accent-primary" checked={selected === o.id} onChange={() => setSelected(o.id)} />
                <span className="min-w-0 flex-1"><span className="block text-sm font-medium">{o.name}</span><span className="block text-xs text-muted">{o.hint}</span></span>
              </label>
            </li>
          ))}
        </ul>
      </fieldset>
    </Modal>
  );
}

function MembersDialog({ group, onClose }: { group: Group; onClose: () => void }) {
  const plan = usePlan();
  const { run, busy } = useCommand(plan.id);
  const ms = useQuery({
    queryKey: planKey(plan.id, "memberships"),
    queryFn: async () => (await get<{ data: Membership[] }>(`/plans/${plan.id}/memberships?page_size=100`)).data,
  });
  const [selected, setSelected] = useState(() => new Set(group.members.map((m) => m.membership_id)));
  const toggle = (id: string) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const save = async () => {
    const ok = await run(() => put(`/plans/${plan.id}/groups/${group.id}/members`, { expected_version: group.version, membership_ids: [...selected] }), "Membros atualizados.");
    if (ok) onClose();
  };
  return (
    <Modal open wide onOpenChange={(o) => !o && onClose()} title={`Membros de ${group.name}`} description="Uma pessoa pode participar de vários grupos."
      footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button onClick={() => void save()} loading={busy}>Salvar membros</Button></>}>
      {ms.isPending ? <Skeleton /> : ms.isError ? <ErrorState error={ms.error} /> : (
        <fieldset>
          <legend className="sr-only">Participantes ativos</legend>
          <ul className="max-h-80 divide-y divide-line overflow-y-auto rounded-ctl border border-line">
            {ms.data.filter((m) => m.active).map((m) => (
              <li key={m.id}>
                <label className="elevate flex cursor-pointer items-center gap-3 px-3 py-2.5">
                  <input type="checkbox" className="size-4 accent-primary" checked={selected.has(m.id)} onChange={() => toggle(m.id)} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">{m.name}</span>
                    <span className="block truncate text-xs text-muted">{m.email} · {roleLabel[m.role]}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      )}
    </Modal>
  );
}
